/// <reference path="../../types/declarations.d.ts" />
/// <reference path="../../types/drei.d.ts" />
/// <reference path="../../threejs.d.ts" />
import React, { useRef, useState, useEffect } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';

const AboutHero: React.FC = () => {
  const [isMounted, setIsMounted] = useState(false);
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  
  const y = useTransform(scrollYProgress, [0, 1], [0, 300]);
  const opacity = useTransform(scrollYProgress, [0, 0.8], [1, 0]);
  const scale = useTransform(scrollYProgress, [0, 0.8], [1, 0.8]);
  const rotateX = useTransform(scrollYProgress, [0, 0.8], [0, 10]);

  useEffect(() => {
    setIsMounted(true);
  }, []);

  return (
    <div ref={ref} className="relative h-screen w-full overflow-hidden bg-gradient-to-b from-blue-50 to-pink-50">
      {/* Decorative elements */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute w-full h-full bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTAwIiBoZWlnaHQ9IjEwMCIgdmlld0JveD0iMCAwIDEwMCAxMDAiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+CiAgPGcgb3BhY2l0eT0iMC4xIj4KICAgIDxjaXJjbGUgY3g9IjUwIiBjeT0iNTAiIHI9IjUiIGZpbGw9IiNGRjRFODMiLz4KICAgIDxjaXJjbGUgY3g9IjEwMCIgY3k9IjEwMCIgcj0iNSIgZmlsbD0iIzc5MkJDQSIvPgogICAgPGNpcmNsZSBjeD0iMCIgY3k9IjEwMCIgcj0iNSIgZmlsbD0iI0ZGNEVFMyIvPgogICAgPGNpcmNsZSBjeD0iMTAwIiBjeT0iMCIgcj0iNSIgZmlsbD0iIzUzQjJGRiIvPgogICAgPGNpcmNsZSBjeD0iMCIgY3k9IjAiIHI9IjUiIGZpbGw9IiNGRjRFODMiLz4KICA8L2c+Cjwvc3ZnPg==')] opacity-30"></div>
      </div>
      
      {/* Animated shapes */}
      <motion.div 
        className="absolute top-0 right-0 w-[700px] h-[700px] opacity-20 pointer-events-none"
        initial={{ rotate: 0, y: -100 }}
        animate={{ rotate: 360, y: -80 }}
        transition={{ duration: 40, repeat: Infinity, ease: "linear" }}
      >
        <svg viewBox="0 0 200 200" xmlns="http://www.w3.org/2000/svg">
          <path fill="#FF4E83" d="M46.5,-78.3C59.1,-72.8,68,-57.9,73.9,-42.6C79.8,-27.2,82.7,-11.4,81.5,3.8C80.3,19,75.1,33.6,67.1,47.5C59.1,61.4,48.5,74.6,34.8,79.5C21.1,84.3,4.4,80.9,-10.7,75.5C-25.8,70.1,-39.3,62.8,-53,53.7C-66.7,44.6,-80.6,33.8,-86,19.5C-91.4,5.3,-88.2,-12.4,-81.3,-28.4C-74.4,-44.4,-63.9,-58.7,-50.2,-63.9C-36.5,-69.1,-19.6,-65.2,-1.9,-62.1C15.8,-59,33.9,-56.8,46.5,-78.3Z" transform="translate(100 100)" />
        </svg>
      </motion.div>
      
      <motion.div 
        className="absolute -bottom-20 -left-20 w-[600px] h-[600px] opacity-20 pointer-events-none"
        initial={{ rotate: 0 }}
        animate={{ rotate: -360 }}
        transition={{ duration: 60, repeat: Infinity, ease: "linear" }}
      >
        <svg viewBox="0 0 200 200" xmlns="http://www.w3.org/2000/svg">
          <path fill="#7928CA" d="M39.9,-65.7C54.1,-60.4,69.7,-53.1,77.4,-41C85.1,-28.8,84.9,-11.7,81.5,3.9C78.2,19.6,71.7,34,62.6,47C53.5,60,41.8,71.8,27.7,76C13.6,80.3,-2.9,77.1,-17.4,71.7C-31.9,66.3,-44.5,58.7,-54.8,48.5C-65.1,38.2,-73.2,25.2,-78.2,10.1C-83.2,-5,-85.2,-22.2,-79.6,-36.3C-73.9,-50.3,-60.7,-61.2,-46.1,-66.6C-31.6,-72,-15.8,-71.9,-0.3,-71.4C15.2,-70.9,30.4,-70,39.9,-65.7Z" transform="translate(100 100)" />
        </svg>
      </motion.div>
      
      {/* Cherry blossom particles */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {Array.from({ length: 20 }).map((_, i) => (
          <motion.div
            key={i}
            className="absolute w-6 h-6 opacity-70"
            style={{
              left: `${Math.random() * 100}%`,
              top: `-5%`,
              backgroundImage: "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 100 100'%3E%3Cpath d='M50,15 C55,25 65,35 75,35 C65,40 65,50 75,65 C65,65 55,75 50,85 C45,75 35,65 25,65 C35,50 35,40 25,35 C35,35 45,25 50,15' fill='%23FF4E83' /%3E%3C/svg%3E\")",
              backgroundSize: "contain",
              backgroundRepeat: "no-repeat"
            }}
            animate={{
              y: [`${-10}%`, `${110}%`],
              x: [0, Math.random() * 100 - 50],
              rotate: [0, 360]
            }}
            transition={{
              duration: 10 + Math.random() * 15,
              repeat: Infinity,
              ease: "linear",
              delay: Math.random() * 20
            }}
          />
        ))}
      </div>
      
      {/* Content */}
      <div className="container mx-auto px-4 md:px-6 relative z-20 h-full flex flex-col justify-center items-center">
        <motion.div 
          className="text-center perspective"
          style={{ opacity }}
          initial={{ opacity: 1, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
        >
          <motion.div
            className="mb-4"
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <img 
              src="/logo.png" 
              alt="Nihon Bridge Logo" 
              className="h-20 mx-auto" 
              onError={(e) => {
                const target = e.target as HTMLImageElement;
                target.onerror = null;
                target.src = "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='100' height='100' viewBox='0 0 100 100'%3E%3Crect width='100' height='100' fill='%23f0f4f8'/%3E%3Cpath d='M30,50 L70,50 M50,30 L50,70' stroke='%234f46e5' stroke-width='5'/%3E%3C/svg%3E";
              }}
            />
          </motion.div>
          
          <motion.h1 
            className="text-6xl md:text-8xl font-bold mb-8 tracking-tight drop-shadow-md"
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ 
              duration: 0.8, 
              delay: 0.2,
              type: "spring",
              stiffness: 100
            }}
          >
            <span className="relative inline-block">
              <span className="text-indigo-700">Recall</span>
              <motion.div 
                className="absolute -bottom-1 left-0 w-full h-3 bg-gradient-to-r from-pink-500 to-indigo-600 rounded-full"
                initial={{ width: 0 }}
                animate={{ width: "100%" }}
                transition={{ duration: 1, delay: 0.8 }}
              />
            </span>{" "}
            <span className="relative inline-block mt-2 md:mt-0">
              <span className="text-pink-600">Nepal</span>
              <motion.div 
                className="absolute -bottom-1 left-0 w-full h-3 bg-gradient-to-r from-indigo-500 to-pink-600 rounded-full"
                initial={{ width: 0 }}
                animate={{ width: "100%" }}
                transition={{ duration: 1, delay: 1.3 }}
              />
            </span>
          </motion.h1>
          
          <motion.div
            className="relative mb-8 mx-auto w-40 h-40 md:w-60 md:h-60 z-20"
            initial={{ opacity: 0, scale: 0 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1, delay: 0.5 }}
          >
            <div className="absolute inset-0 rounded-full bg-gradient-to-r from-indigo-600 to-pink-600 animate-pulse"></div>
            <div className="absolute inset-1 rounded-full bg-white flex items-center justify-center">
              <div className="text-6xl md:text-7xl">🌉</div>
            </div>
          </motion.div>
          
          <motion.p
            className="text-xl md:text-2xl text-gray-800 font-medium max-w-3xl mx-auto leading-relaxed drop-shadow-sm"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8, delay: 0.7 }}
          >
            Connecting cultures and futures since 2018. At Recall Nepal, we've helped over 2,000+ students
            transform their Japanese education dreams into successful global careers.
          </motion.p>
          
          {/* <motion.div 
            className="mt-10 flex flex-wrap justify-center gap-4"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.9 }}
          >
            <motion.button 
              className="px-8 py-4 bg-gradient-to-r from-indigo-600 to-indigo-800 text-white font-bold rounded-full shadow-xl hover:shadow-indigo-500/30 transition-all"
              whileHover={{ 
                scale: 1.05, 
                boxShadow: "0 15px 30px rgba(99, 102, 241, 0.4)",
                y: -5
              }}
              whileTap={{ scale: 0.98 }}
            >
              Our Story
            </motion.button>
            <motion.button 
              className="px-8 py-4 bg-white text-pink-600 font-bold rounded-full shadow-xl border-2 border-pink-600 hover:shadow-pink-500/20 transition-all"
              whileHover={{ 
                scale: 1.05, 
                boxShadow: "0 15px 30px rgba(236, 72, 153, 0.2)",
                y: -5
              }}
              whileTap={{ scale: 0.98 }}
            >
              Meet Our Team
            </motion.button>
          </motion.div> */}
        </motion.div>
      </div>
      
      {/* Scroll indicator */}
      {/* <motion.div 
        className="absolute bottom-8 left-1/2 transform -translate-x-1/2 flex flex-col items-center text-indigo-600 z-20"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.5, duration: 1 }}
      >
        <p className="text-sm font-medium mb-2">Scroll to explore</p>
        <motion.div 
          className="w-6 h-10 border-2 border-indigo-600 rounded-full flex justify-center p-1"
          animate={{ y: [0, 10, 0] }}
          transition={{ repeat: Infinity, duration: 1.5 }}
        >
          <motion.div className="w-2 h-2 bg-indigo-600 rounded-full" />
        </motion.div>
      </motion.div> */}
    </div>
  );
};

export default AboutHero; 